import React, { useState, useEffect } from 'react';
import { 
  ShoppingCart, 
  Plus, 
  Minus, 
  Trash2, 
  CreditCard, 
  Package,
  Star,
  Heart,
  Share2,
  Eye,
  Clock
} from 'lucide-react';
import { ToastContext } from '../ToastProvider';
import { useAuth } from '../../AuthContext';

export default function LiveShoppingCart({ 
  sessionId, 
  affiliateId, 
  isLive,
  onCartUpdate 
}) {
  const { user } = useAuth();
  const { notify } = React.useContext(ToastContext);
  
  const [cartItems, setCartItems] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [cartTotal, setCartTotal] = useState(0);
  const [itemCount, setItemCount] = useState(0);

  // Mock products for live selling
  const mockProducts = [
    {
      id: 'prod1',
      name: 'Premium Coffee Beans',
      price: 299.00,
      image: 'https://images.unsplash.com/photo-1559056199-641a0ac8b55e?w=200&h=200&fit=crop',
      description: 'Fresh roasted coffee beans from local farms',
      category: 'Food & Beverages',
      rating: 4.8,
      reviews: 156,
      affiliateCommission: 15.00
    },
    {
      id: 'prod2',
      name: 'Organic Honey',
      price: 199.00,
      image: 'https://images.unsplash.com/photo-1587049352846-4a222e784d38?w=200&h=200&fit=crop',
      description: 'Pure organic honey from mountain bees',
      category: 'Food & Beverages',
      rating: 4.9,
      reviews: 89,
      affiliateCommission: 10.00
    },
    {
      id: 'prod3',
      name: 'Handwoven Basket',
      price: 450.00,
      image: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=200&h=200&fit=crop',
      description: 'Traditional handwoven basket made by local artisans',
      category: 'Handicrafts',
      rating: 4.7,
      reviews: 23,
      affiliateCommission: 22.50
    },
    {
      id: 'prod4',
      name: 'Herbal Tea Set',
      price: 350.00,
      image: 'https://images.unsplash.com/photo-1556679343-c7306c1976bc?w=200&h=200&fit=crop',
      description: 'Collection of premium herbal teas',
      category: 'Food & Beverages',
      rating: 4.6,
      reviews: 67,
      affiliateCommission: 17.50
    }
  ];

  // Calculate cart totals
  useEffect(() => {
    const total = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const count = cartItems.reduce((sum, item) => sum + item.quantity, 0);
    setCartTotal(total);
    setItemCount(count);
    onCartUpdate?.(cartItems, total, count);
  }, [cartItems, onCartUpdate]);

  const addToCart = (product) => {
    if (!user) {
      notify('Please log in to add items to cart', { type: 'warning' });
      return;
    }

    const existingItem = cartItems.find(item => item.id === product.id);
    
    if (existingItem) {
      setCartItems(prev => 
        prev.map(item => 
          item.id === product.id 
            ? { ...item, quantity: item.quantity + 1 }
            : item
        )
      );
    } else {
      setCartItems(prev => [...prev, { ...product, quantity: 1 }]);
    }
    
    notify(`${product.name} added to cart!`, { type: 'success' });
  };

  const updateQuantity = (productId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(productId);
      return;
    }
    
    setCartItems(prev => 
      prev.map(item => 
        item.id === productId 
          ? { ...item, quantity: newQuantity }
          : item
      )
    );
  };

  const removeFromCart = (productId) => {
    setCartItems(prev => prev.filter(item => item.id !== productId));
    notify('Item removed from cart', { type: 'info' });
  };

  const clearCart = () => {
    setCartItems([]);
    notify('Cart cleared', { type: 'info' });
  };

  const handleCheckout = async () => {
    if (!user) {
      notify('Please log in to checkout', { type: 'warning' });
      return;
    }

    if (cartItems.length === 0) {
      notify('Your cart is empty', { type: 'warning' });
      return;
    }

    setIsCheckingOut(true);
    
    try {
      // Simulate checkout process
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Here you would integrate with your payment system
      notify('Order placed successfully!', { type: 'success' });
      
      // Clear cart after successful checkout
      setCartItems([]);
      
    } catch (error) {
      notify('Checkout failed. Please try again.', { type: 'error' });
    } finally {
      setIsCheckingOut(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP'
    }).format(amount);
  };

  return (
    <div className="border-t border-gray-200 bg-white">
      {/* Cart Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <ShoppingCart className="w-5 h-5 text-blue-600" />
            <h3 className="font-semibold text-gray-900">Live Shopping</h3>
            {itemCount > 0 && (
              <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                {itemCount}
              </span>
            )}
          </div>
          <button
            onClick={() => setShowCart(!showCart)}
            className="text-blue-600 hover:text-blue-700 text-sm font-medium"
          >
            {showCart ? 'Hide Cart' : 'View Cart'}
          </button>
        </div>
      </div>

      {/* Products Grid */}
      <div className="p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {mockProducts.map((product) => (
            <div key={product.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
              <div className="aspect-square bg-gray-100 relative">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-2 right-2">
                  <button className="p-1 bg-white/80 rounded-full hover:bg-white transition-colors">
                    <Heart className="w-4 h-4 text-gray-600" />
                  </button>
                </div>
                <div className="absolute top-2 left-2">
                  <span className="bg-green-500 text-white text-xs px-2 py-1 rounded">
                    Live
                  </span>
                </div>
              </div>
              
              <div className="p-3">
                <div className="flex items-center space-x-1 mb-1">
                  <Star className="w-3 h-3 text-yellow-500 fill-current" />
                  <span className="text-xs text-gray-600">{product.rating}</span>
                  <span className="text-xs text-gray-400">({product.reviews})</span>
                </div>
                
                <h4 className="font-medium text-sm text-gray-900 mb-1 line-clamp-2">
                  {product.name}
                </h4>
                
                <p className="text-xs text-gray-600 mb-2 line-clamp-2">
                  {product.description}
                </p>
                
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold text-blue-600">
                    {formatCurrency(product.price)}
                  </span>
                  <span className="text-xs text-green-600">
                    +{formatCurrency(product.affiliateCommission)}
                  </span>
                </div>
                
                <button
                  onClick={() => addToCart(product)}
                  className="w-full py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors flex items-center justify-center space-x-1"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add to Cart</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Cart Sidebar */}
      {showCart && (
        <div className="border-t border-gray-200 bg-gray-50">
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-semibold text-gray-900">Your Cart</h4>
              {cartItems.length > 0 && (
                <button
                  onClick={clearCart}
                  className="text-red-600 hover:text-red-700 text-sm"
                >
                  Clear All
                </button>
              )}
            </div>

            {cartItems.length === 0 ? (
              <div className="text-center py-8">
                <ShoppingCart className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p className="text-gray-500 mb-2">Your cart is empty</p>
                <p className="text-sm text-gray-400">Add products from above to get started!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {cartItems.map((item) => (
                  <div key={item.id} className="bg-white p-3 rounded-lg border border-gray-200">
                    <div className="flex items-center space-x-3">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-12 h-12 object-cover rounded"
                      />
                      <div className="flex-1 min-w-0">
                        <h5 className="font-medium text-sm text-gray-900 truncate">
                          {item.name}
                        </h5>
                        <p className="text-sm text-gray-600">
                          {formatCurrency(item.price)} each
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          className="p-1 bg-gray-100 rounded hover:bg-gray-200"
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="text-sm font-medium w-8 text-center">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          className="p-1 bg-gray-100 rounded hover:bg-gray-200"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => removeFromCart(item.id)}
                          className="p-1 text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <span className="text-sm text-gray-600">
                        Subtotal: {formatCurrency(item.price * item.quantity)}
                      </span>
                      <span className="text-xs text-green-600">
                        Commission: {formatCurrency(item.affiliateCommission * item.quantity)}
                      </span>
                    </div>
                  </div>
                ))}

                {/* Cart Summary */}
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span>Subtotal:</span>
                      <span>{formatCurrency(cartTotal)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Shipping:</span>
                      <span>{formatCurrency(50)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Tax:</span>
                      <span>{formatCurrency(cartTotal * 0.12)}</span>
                    </div>
                    <div className="border-t pt-2">
                      <div className="flex justify-between font-semibold">
                        <span>Total:</span>
                        <span>{formatCurrency(cartTotal + 50 + (cartTotal * 0.12))}</span>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={handleCheckout}
                    disabled={isCheckingOut}
                    className="w-full py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
                  >
                    {isCheckingOut ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Processing...</span>
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-4 h-4" />
                        <span>Checkout</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
